import {
    Box,
    Group,
    Modal,
    Paper,
    ScrollArea,
    Stack,
    Tabs,
    Text,
} from '@mantine/core'
import ReactMarkdown from 'react-markdown'

interface ConversationSummaryModalProps {
  opened: boolean
  onClose: () => void
  summaryData: any
  loading: boolean
}

export function ConversationSummaryModal({
  opened,
  onClose,
  summaryData,
  loading,
}: ConversationSummaryModalProps) {
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Conversation Summary"
      size={800}
      styles={{
        body: {
          height: '600px',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {loading ? (
        <Stack align="center" p="xl">
          <Text>Loading conversation summary...</Text>
        </Stack>
      ) : summaryData ? (
        <Tabs defaultValue="crm" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Tabs.List>
            <Tabs.Tab value="crm">CRM Summary</Tabs.Tab>
            <Tabs.Tab value="workflow">Workflow Summary</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="crm" pt="md" style={{ flex: 1, overflow: 'hidden' }}>
            {summaryData.conversationSummary ? (
              <Box style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <ScrollArea style={{ flex: 1 }}>
                  <Paper p="xl" withBorder>
                    <ReactMarkdown
                      components={{
                        h1: ({ children, ...props }: any) => <Text size="xl" fw={700} mb="sm" {...props}>{children}</Text>,
                        h2: ({ children, ...props }: any) => <Text size="lg" fw={600} mb="sm" {...props}>{children}</Text>,
                        h3: ({ children, ...props }: any) => <Text size="md" fw={600} mb="xs" {...props}>{children}</Text>,
                        p: ({ children, ...props }: any) => <Text size="sm" mb="sm" {...props}>{children}</Text>,
                        ul: ({ children, ...props }: any) => <Box component="ul" pl="md" mb="sm" {...props}>{children}</Box>,
                        ol: ({ children, ...props }: any) => <Box component="ol" pl="md" mb="sm" {...props}>{children}</Box>,
                        li: ({ children, ...props }: any) => <Text component="li" size="sm" mb="xs" {...props}>{children}</Text>,
                        strong: ({ children, ...props }: any) => <Text component="strong" fw={600} {...props}>{children}</Text>,
                        em: ({ children, ...props }: any) => <Text component="em" fs="italic" {...props}>{children}</Text>,
                      }}
                    >
                      {summaryData.conversationSummary}
                    </ReactMarkdown>
                  </Paper>
                </ScrollArea>
                <Group gap="md" mt="xs">
                  <Text size="xs" c="dimmed">
                    Generated:{' '}
                    {summaryData.summaryGeneratedAt
                      ? new Date(summaryData.summaryGeneratedAt).toLocaleString()
                      : 'Unknown'}
                  </Text>
                  <Text size="xs" c="dimmed">
                    Posted to CRM: {summaryData.summaryPostedToCrm ? 'Yes' : 'No'}
                  </Text>
                </Group>
              </Box>
            ) : (
              <Text c="dimmed" ta="center" py="xl">
                No CRM summary available for this conversation yet.
              </Text>
            )}
          </Tabs.Panel>

          <Tabs.Panel value="workflow" pt="md" style={{ flex: 1, overflow: 'hidden' }}>
            {summaryData.workflowSummary ? (
              <Paper p="md" withBorder style={{ height: '100%' }}>
                <ScrollArea h="100%">
                  <Text size="sm" component="pre" style={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                    {JSON.stringify(summaryData.workflowSummary, null, 2)}
                  </Text>
                </ScrollArea>
              </Paper>
            ) : (
              <Text c="dimmed" ta="center" py="xl">
                No workflow summary available for this conversation yet.
              </Text>
            )}
          </Tabs.Panel>
        </Tabs>
      ) : (
        <Text c="dimmed" ta="center">
          Failed to load conversation summary.
        </Text>
      )}
    </Modal>
  )
}

